if (!customElements.get('shop-the-look-drawer')) {
  /**
   * @classdesc Drawer body for the Shop The Look Slider section (GC-2468).
   * Stamps the bundle UI from a <template> on first open (so bundle items
   * don't fetch variant data on page load), and drives the "quickshop"
   * sub-view: tapping a product image enlarges that product-bundle-item in
   * place while hiding its siblings — same DOM, same selection state, and
   * the drawer's existing focus trap stays valid because hidden elements
   * are filtered out per keydown.
   * @extends HTMLElement
   */
  class ShopTheLookDrawer extends HTMLElement {
    connectedCallback() {
      // Re-runs when the ancestor slide-out-drawer moves itself to <body>;
      // the controller keeps listeners from doubling up.
      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.drawer = this.closest('slide-out-drawer');
      this.template = this.querySelector('template[data-stl-drawer-content]');
      this.quickshopClose = this.querySelector('.stl-look-drawer__quickshop-close');
      this.drawerClose = this.querySelector('.slide-out-drawer__close');
      this.activeItem = null;
      this.returnFocusTo = null;

      this.drawer?.addEventListener('slide-out-drawer:open', () => this.stampContent(), { signal });
      this.drawer?.addEventListener('slide-out-drawer:close', () => this.closeQuickshop(false), { signal });
      this.addEventListener('click', this.handleClick.bind(this), { signal });
      this.quickshopClose?.addEventListener('click', () => this.closeQuickshop(true), { signal });
      // While the quickshop sub-view is open, Escape steps back to the look
      // instead of dismissing the whole drawer. Capture phase so this runs
      // before the drawer's own document-level Escape listener.
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.activeItem) {
          event.stopPropagation();
          this.closeQuickshop(true);
        }
      }, { capture: true, signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
    }

    stampContent() {
      if (!this.template) return;
      const content = this.template.content.cloneNode(true);
      this.template.replaceWith(content);
      this.template = null;

      this.overview = this.querySelector('product-bundle-overview');
      // The overview only recalculates on variant *changes*; nudge it as
      // items finish initializing so the combined price and add-all button
      // state are correct on first open.
      this.addEventListener('product-bundle-item:initialized', () => this.refreshOverview(), { signal: this.abort.signal });
      this.injectQuickshopButtons();
    }

    refreshOverview() {
      const overview = this.overview;
      if (typeof overview?.setBundleVariants !== 'function') return;
      overview.setBundleVariants();
      overview.updatePriceElements?.();
      overview.updateBundleATC?.();
    }

    injectQuickshopButtons() {
      // The bundle item's media anchor is aria-hidden/tabindex="-1", so a
      // real button is layered over it — keyboard users get a Tab stop and
      // pointer users get the same tap target the mock shows.
      for (const item of this.querySelectorAll('product-bundle-item')) {
        const media = item.querySelector('.bundle-product__media');
        if (!media || media.querySelector('.stl-quickshop-open')) continue;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'stl-quickshop-open';
        button.setAttribute('aria-label', `View ${item.dataset.productTitle || 'product'} details`);
        media.appendChild(button);
      }
    }

    handleClick(event) {
      const trigger = event.target.closest('.stl-quickshop-open');
      if (!trigger) return;
      event.preventDefault();
      const item = trigger.closest('product-bundle-item');
      if (item) this.openQuickshop(item, trigger);
    }

    openQuickshop(item, trigger) {
      if (this.activeItem) return;
      this.activeItem = item;
      this.returnFocusTo = trigger;
      item.classList.add('stl-quickshop-item');
      this.setAttribute('data-quickshop', '');
      if (this.quickshopClose) this.quickshopClose.hidden = false;
      if (this.drawerClose) this.drawerClose.hidden = true;
      this.querySelector('.slide-out-drawer__content')?.scrollTo({ top: 0 });
      this.quickshopClose?.focus();
    }

    closeQuickshop(restoreFocus) {
      if (!this.activeItem) return;
      this.activeItem.classList.remove('stl-quickshop-item');
      this.removeAttribute('data-quickshop');
      if (this.quickshopClose) this.quickshopClose.hidden = true;
      if (this.drawerClose) this.drawerClose.hidden = false;
      if (restoreFocus) (this.returnFocusTo || this.drawerClose)?.focus();
      this.activeItem = null;
      this.returnFocusTo = null;
    }
  }

  customElements.define('shop-the-look-drawer', ShopTheLookDrawer);
}
